/**
 * Signed URL cache with batch signing.
 *
 * When auth is enabled, all /derivatives URLs must be signed via HMAC.
 * This module coalesces individual URL requests into batch /media/sign calls,
 * caches the results, and triggers re-renders when signed URLs arrive.
 *
 * Pattern follows AWS S3 / GCS pre-signed URL model.
 * CDN Migration: Replace /media/sign with CDN's built-in signing.
 * See docs/plans/CDN_Edge_Migration.md
 */

import { AUTH_ENABLED } from '@/auth/config'
import { getToken } from '@/auth/tokenStore'

// BFF origin for /media/sign endpoint (same origin in local dev, explicit URL for CDN)
const BFF_URL = import.meta.env.VITE_BFF_URL || ''

interface CacheEntry {
  url: string
  expiresAt: number
}

const cache = new Map<string, CacheEntry>()
const pending = new Set<string>()
let batchPaths: string[] = []
let batchTimer: ReturnType<typeof setTimeout> | null = null
let subscribers: Array<() => void> = []

/** Coalescing window — paths requested within this window are batched together. */
const BATCH_DELAY_MS = 50

/** Refresh signed URLs this many seconds before they expire. */
const REFRESH_MARGIN_S = 300

/** 1x1 transparent GIF — renders instantly, no network request, no onerror. */
const LOADING_PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

/**
 * Get a signed URL for a derivatives path.
 *
 * Returns the cached signed URL if available and not expiring soon.
 * Otherwise queues the path for batch signing and returns a transparent
 * placeholder (avoids 403s from unsigned requests that race with signing).
 *
 * Components using this should also call useSignedUrls() to re-render
 * when signed URLs arrive.
 */
export function getSignedUrl(path: string): string {
  // No signing needed when auth is disabled (local dev)
  if (!AUTH_ENABLED) return path

  // Empty/missing paths — pass through
  if (!path) return path

  const entry = cache.get(path)
  const now = Date.now() / 1000

  // Cache hit — return if not about to expire
  if (entry && entry.expiresAt > now + REFRESH_MARGIN_S) {
    return entry.url
  }

  // Queue for batch signing (if not already pending)
  if (!pending.has(path)) {
    pending.add(path)
    batchPaths.push(path)
    scheduleBatch()
  }

  // Return best available: stale signed URL > transparent placeholder
  // Using a data URI avoids 403 errors from unsigned requests racing with the batch.
  return entry?.url ?? LOADING_PLACEHOLDER
}

/**
 * Subscribe to cache updates. Returns an unsubscribe function.
 * Used by useSignedUrls() hook to trigger re-renders.
 */
export function subscribe(fn: () => void): () => void {
  subscribers.push(fn)
  return () => {
    subscribers = subscribers.filter((s) => s !== fn)
  }
}

function scheduleBatch(): void {
  if (batchTimer) return
  batchTimer = setTimeout(executeBatch, BATCH_DELAY_MS)
}

async function executeBatch(): Promise<void> {
  const paths = [...batchPaths]
  batchPaths = []
  batchTimer = null
  paths.forEach((p) => pending.delete(p))

  if (paths.length === 0) return

  try {
    const token = await getToken()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const resp = await fetch(`${BFF_URL}/media/sign`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ paths }),
    })

    if (!resp.ok) {
      console.warn('[signedUrlCache] Sign request failed:', resp.status)
      return
    }

    const data: { signed: Record<string, string>; expires_at: number } = await resp.json()

    for (const [path, signedUrl] of Object.entries(data.signed)) {
      cache.set(path, { url: signedUrl, expiresAt: data.expires_at })
    }

    // Notify subscribers to trigger re-renders with signed URLs
    subscribers.forEach((fn) => fn())
  } catch (err) {
    console.warn('[signedUrlCache] Sign request error:', err)
  }
}
